import { useCallback, useEffect, useRef, useState } from "react";
export function usePanelResize({
  value,
  min,
  max,
  widthFromClientX,
  onCommit
}: {
  value: number;
  min: number;
  max: number;
  widthFromClientX: (clientX: number) => number;
  onCommit: (width: number) => void;
}) {
  const config = useRef({
    value,
    min,
    max,
    widthFromClientX,
    onCommit
  });
  config.current = {
    value,
    min,
    max,
    widthFromClientX,
    onCommit
  };
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const pendingWidth = useRef(value);
  const activePointer = useRef<number | null>(null);
  const frame = useRef<number | null>(null);
  const clamp = useCallback((width: number) => {
    const current = config.current;
    return Math.max(current.min, Math.min(current.max, width));
  }, []);
  const cancelFrame = useCallback(() => {
    if (frame.current === null) return;
    cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);
  useEffect(() => cancelFrame, [cancelFrame]);
  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    activePointer.current = event.pointerId;
    pendingWidth.current = clamp(config.current.value);
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragWidth(pendingWidth.current);
  }, [clamp]);
  const onPointerMove = useCallback((event_0: React.PointerEvent<HTMLDivElement>) => {
    if (activePointer.current !== event_0.pointerId) return;
    pendingWidth.current = clamp(config.current.widthFromClientX(event_0.clientX));
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      setDragWidth(pendingWidth.current);
      frame.current = null;
    });
  }, [clamp]);
  const finish = useCallback((event_1: {
    pointerId: number;
    clientX: number;
  }, commit: boolean, handle?: HTMLElement) => {
    if (activePointer.current !== event_1.pointerId) return;
    if (commit) {
      pendingWidth.current = clamp(config.current.widthFromClientX(event_1.clientX));
      config.current.onCommit(pendingWidth.current);
    }
    cancelFrame();
    activePointer.current = null;
    if (handle?.hasPointerCapture(event_1.pointerId)) {
      handle.releasePointerCapture(event_1.pointerId);
    }
    setDragWidth(null);
  }, [cancelFrame, clamp]);
  const isResizing = dragWidth !== null;

  // Pointer capture ends the drag on the handle; if the handle unmounts
  // mid-drag (pane closed, summary switched to overlay) the release fires
  // on whatever is under the pointer instead, so the document catches it.
  useEffect(() => {
    if (!isResizing) return;
    const onUp = (event_2: PointerEvent) => finish(event_2, true);
    const onCancel = (event_3: PointerEvent) => finish(event_3, false);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onCancel);
    return () => {
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onCancel);
    };
  }, [isResizing, finish]);
  const onPointerUp = useCallback((event_4: React.PointerEvent<HTMLDivElement>) => finish(event_4, true, event_4.currentTarget), [finish]);
  const onPointerCancel = useCallback((event_5: React.PointerEvent<HTMLDivElement>) => finish(event_5, false, event_5.currentTarget), [finish]);
  const setValue = useCallback((next: number) => config.current.onCommit(clamp(next)), [clamp]);
  return {
    value: dragWidth ?? value,
    dragWidth,
    isResizing,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    setValue
  };
}